import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Layout from './components/Layout'
import ScrollToTop from './components/ScrollToTop'
import { ACTIVE_DESIGN_SYSTEM } from './config/designSystem'
import HomePage from './pages/HomePage'
import CoursesPage from './pages/CoursesPage'
import CourseDetailPage from './pages/CourseDetailPage'
import AboutPage from './pages/AboutPage'
import BlogHomepage from './pages/blog/BlogHomepage'
import TopicPage from './pages/blog/TopicPage'
import ArticleDetailPage from './pages/blog/ArticleDetailPage'
import RegisterPage from './pages/auth/RegisterPage'
import LoginPage from './pages/auth/LoginPage'
import CompletePhonePage from './pages/auth/CompletePhonePage'
import SecurityPolicyPage from './pages/SecurityPolicyPage'
import TermsOfServicePage from './pages/TermsOfServicePage'
import ContactPage from './pages/ContactPage'
import ProtectedRoute from './components/auth/ProtectedRoute'
import AdminLayout from './pages/admin/AdminLayout'
import AdminRegistrationListPage from './pages/admin/AdminRegistrationListPage'
import AdminEnterpriseRegistrationPage from './pages/admin/AdminEnterpriseRegistrationPage'
import AdminEnterpriseBatchListPage from './pages/admin/AdminEnterpriseBatchListPage'
import AdminMisaInvoiceLogsPage from './pages/admin/AdminMisaInvoiceLogsPage'
import AdminCoursesPage from './pages/admin/AdminCoursesPage'
import AdminCourseDetailPage from './pages/admin/AdminCourseDetailPage'
import AdminCourseFormPage from './pages/admin/AdminCourseFormPage'
import AdminSkillLibraryPage from './pages/admin/AdminSkillLibraryPage'
import AdminVideosPage from './pages/admin/AdminVideosPage'
import AdminPostsPage from './pages/admin/AdminPostsPage'
import AdminPostFormPage from './pages/admin/AdminPostFormPage'
import AdminWarningsPage from './pages/admin/AdminWarningsPage'
import AdminUsersPage from './pages/admin/AdminUsersPage'
import AdminReviewsPage from './pages/admin/AdminReviewsPage'
import AdminTestResultsPage from './pages/admin/AdminTestResultsPage'
import AdminTestResultDetailPage from './pages/admin/AdminTestResultDetailPage'
import AdminHomeworkHubPage from './pages/admin/AdminHomeworkHubPage'
import AdminHomeworkReportPage from './pages/admin/AdminHomeworkReportPage'
import AdminHomeworkSubmissionsPage from './pages/admin/AdminHomeworkSubmissionsPage'
import AdminHomeworkSubmissionDetailPage from './pages/admin/AdminHomeworkSubmissionDetailPage'
import AdminTestsPage from './pages/admin/AdminTestsPage'
import AdminTestDetailPage from './pages/admin/AdminTestDetailPage'
import AdminTestComposePage from './pages/admin/AdminTestComposePage'
import AdminQuestionBankPage from './pages/admin/AdminQuestionBankPage'
import AdminQuestionFormPage from './pages/admin/AdminQuestionFormPage'
import AdminQuestionGroupsPage from './pages/admin/AdminQuestionGroupsPage'
import MyCoursesPage from './pages/student/MyCoursesPage'
import StudentCourseDetailPage from './pages/student/StudentCourseDetailPage'
import StudentLearningPage from './pages/student/StudentLearningPage'
import StudentHomeworkDoPage from './pages/student/StudentHomeworkDoPage'
import StudentHomeworkHubPage from './pages/student/StudentHomeworkHubPage'
import SkillLibraryPage from './pages/student/SkillLibraryPage'
import WorkshopPage from './pages/WorkshopPage'
import WorkshopsListPage from './pages/WorkshopsListPage'
import WorkshopDetailPage from './pages/WorkshopDetailPage'
import AdminWorkshopRegistrationsPage from './pages/admin/AdminWorkshopRegistrationsPage'
import AdminWorkshopsPage from './pages/admin/AdminWorkshopsPage'

function App() {
  useEffect(() => {
    document.documentElement.setAttribute('data-design-system', ACTIVE_DESIGN_SYSTEM)
  }, [])

  return (
    <Router>
      <ScrollToTop />
      
      <Routes>
        {/* Learning Interface does not use the common Layout to have full screen */}
        <Route path="/student/learn/:id" element={
          <ProtectedRoute>
            <StudentLearningPage />
          </ProtectedRoute>
        } />

        <Route path="/*" element={
          <Layout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/courses/:hashId" element={<CourseDetailPage />} />
              <Route path="/blog" element={<BlogHomepage />} />
              <Route path="/blog/topics/:slug" element={<TopicPage />} />
              <Route path="/blog/articles/:uuid" element={<ArticleDetailPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/workshop" element={<WorkshopPage />} />
              <Route path="/workshops" element={<WorkshopsListPage />} />
              <Route path="/workshops/:slug" element={<WorkshopDetailPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/complete-phone" element={<CompletePhonePage />} />
              <Route path="/security-policy" element={<SecurityPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/contact" element={<ContactPage />} />

              {/* Student Routes */}
              <Route path="/student/my-courses" element={
                <ProtectedRoute>
                  <MyCoursesPage />
                </ProtectedRoute>
              } />
              <Route path="/student/courses/:id" element={
                <ProtectedRoute>
                  <StudentCourseDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/student/courses/:courseId/homeworks/:homeworkId" element={
                <ProtectedRoute>
                  <StudentHomeworkDoPage />
                </ProtectedRoute>
              } />
              <Route path="/student/homeworks" element={
                <ProtectedRoute>
                  <StudentHomeworkHubPage />
                </ProtectedRoute>
              } />
              <Route path="/skills" element={<SkillLibraryPage />} />
              <Route path="/skills/:catalogId" element={<SkillLibraryPage />} />

              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="courses" replace />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="registrations" element={<AdminRegistrationListPage />} />
                <Route path="enterprise-batches" element={<AdminEnterpriseBatchListPage />} />
                <Route path="enterprise-registrations/new" element={<AdminEnterpriseRegistrationPage />} />
                <Route path="misa-invoice-logs" element={<AdminMisaInvoiceLogsPage />} />
                <Route path="courses" element={<AdminCoursesPage />} />
                <Route path="courses/new" element={<AdminCourseFormPage />} />
                <Route path="courses/:id/edit" element={<AdminCourseFormPage />} />
                <Route path="courses/:id" element={<AdminCourseDetailPage />} />
                <Route path="homework-submissions" element={<AdminHomeworkHubPage />} />
                <Route path="homework-reports" element={<AdminHomeworkReportPage />} />
                <Route path="courses/:id/homeworks/:homeworkId/submissions" element={<AdminHomeworkSubmissionsPage />} />
                <Route path="courses/:id/homeworks/:homeworkId/submissions/:submissionId" element={<AdminHomeworkSubmissionDetailPage />} />
                <Route path="skills" element={<AdminSkillLibraryPage />} />
                <Route path="videos" element={<AdminVideosPage />} />
                <Route path="posts" element={<AdminPostsPage />} />
                <Route path="posts/new" element={<AdminPostFormPage />} />
                <Route path="posts/:id/edit" element={<AdminPostFormPage />} />
                <Route path="reviews" element={<AdminReviewsPage />} />
                <Route path="question-bank" element={<AdminQuestionBankPage />} />
                <Route path="question-bank/groups" element={<AdminQuestionGroupsPage />} />
                <Route path="question-bank/new" element={<AdminQuestionFormPage />} />
                <Route path="question-bank/:id/edit" element={<AdminQuestionFormPage />} />
                <Route path="tests" element={<AdminTestsPage />} />
                <Route path="tests/new" element={<AdminTestComposePage />} />
                <Route path="tests/:paperId/edit" element={<AdminTestComposePage />} />
                <Route path="tests/:paperId" element={<AdminTestDetailPage />} />
                <Route path="test-results" element={<AdminTestResultsPage />} />
                <Route path="test-results/:resultId" element={<AdminTestResultDetailPage />} />
                <Route path="warnings" element={<AdminWarningsPage />} />
                <Route path="workshop-registrations" element={<AdminWorkshopRegistrationsPage />} />
                <Route path="workshops" element={<AdminWorkshopsPage />} />
              </Route>
            </Routes>
          </Layout>
        } />
      </Routes>
      <ToastContainer
        theme={ACTIVE_DESIGN_SYSTEM === 'ease' ? 'light' : 'dark'}
        toastClassName={ACTIVE_DESIGN_SYSTEM === 'ease' ? 'border border-eh-border' : undefined}
      />
    </Router>
  )
}

export default App